import { create } from 'zustand'
import type { Session, User as SupabaseUser } from '@supabase/supabase-js'
import toast from 'react-hot-toast'
import { supabase } from '@/lib/supabaseClient'
import { apiService } from '@/services/apiService'
import type { User } from '@/types'

/**
 * Format any error (axios, plain Error, anything) into a stable string that
 * survives minification. Production builds rename class names to single
 * letters, so `console.error('label:', err)` against an AxiosError used to
 * print the cryptic "label: T". This expands the parts that actually carry
 * diagnostic value: message, http status, error code, and the request URL.
 */
const describeError = (err: unknown): string => {
  if (!err) return 'unknown error (falsy)'
  const anyErr = err as Record<string, any>
  const msg = anyErr.message || String(err)
  const status = anyErr.response?.status
  const code = anyErr.code
  const url = anyErr.config?.url
  const parts: string[] = [msg]
  if (status !== undefined) parts.push(`status=${status}`)
  if (code) parts.push(`code=${code}`)
  if (url) parts.push(`url=${url}`)
  return parts.join(' | ')
}

/**
 * True when an axios error has no `.response` — meaning the request never
 * reached a useful HTTP layer. The most common production cause is a CORS
 * mismatch (the browser receives the response but blocks the JS from
 * reading it, so axios sees "Network Error"). Network outages and
 * unreachable hosts also land here.
 */
const isNetworkOrCorsError = (err: unknown): boolean => {
  const anyErr = err as Record<string, any> | null
  if (!anyErr) return false
  if (anyErr.response) return false
  const code = anyErr.code
  return code === 'ERR_NETWORK' || code === 'ECONNABORTED' || /Network Error/i.test(anyErr.message || '')
}

let networkErrorToastShownAt = 0
const NETWORK_ERROR_TOAST_COOLDOWN_MS = 60_000

/**
 * Show a single user-visible toast for network/CORS failures, throttled so
 * a polling failure doesn't spam the screen. Silent for auth-shape errors
 * (those are handled by the caller's branch).
 */
const maybeNotifyNetworkError = (err: unknown) => {
  if (!isNetworkOrCorsError(err)) return
  const now = Date.now()
  if (now - networkErrorToastShownAt < NETWORK_ERROR_TOAST_COOLDOWN_MS) return
  networkErrorToastShownAt = now
  toast.error("Can't reach the server. Check your connection and try again.")
}

type AuthStatus = 'unknown' | 'authenticated' | 'anonymous' | 'denied'

interface AuthState {
  /** Hydrated profile from our backend (null until /auth/me succeeds) */
  user: User | null
  /**
   * Session-only "replay the tutorial" flag. Lives in the store (not in a
   * hook's local state) so that the ProfilePage's Replay button and the
   * Layout's <TutorialTour /> — which each call useTutorial() separately —
   * observe the same value.
   */
  tutorialReplay: boolean
  /** Current Supabase session (null when signed out) */
  session: Session | null
  supabaseUser: SupabaseUser | null
  accessToken: string | null
  /** Workflow state — drives router decisions */
  status: AuthStatus
  /** True while bootstrapping or running an in-flight auth call */
  isLoading: boolean

  /** Convenience derived flag */
  isAuthenticated: boolean

  // ---- actions
  initializeSession: () => Promise<void>
  signInWithPassword: (email: string, password: string) => Promise<void>
  signInWithMagicLink: (email: string) => Promise<void>
  verifyOtpCode: (email: string, token: string) => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  updateUser: (patch: Partial<User>) => void
  setTutorialReplay: (v: boolean) => void
}

let authSubscription: { unsubscribe: () => void } | null = null

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  tutorialReplay: false,
  session: null,
  supabaseUser: null,
  accessToken: null,
  status: 'unknown',
  isLoading: true,
  isAuthenticated: false,

  initializeSession: async () => {
    set({ isLoading: true })
    try {
      const { data, error } = await supabase.auth.getSession()
      if (error) throw error

      const session = data.session
      if (session) {
        set({
          session,
          supabaseUser: session.user,
          accessToken: session.access_token,
          status: 'authenticated',
          isAuthenticated: true,
        })
        await get().refreshProfile()
      } else {
        set({
          session: null,
          supabaseUser: null,
          accessToken: null,
          user: null,
          status: 'anonymous',
          isAuthenticated: false,
          isLoading: false,
        })
      }

      // Single subscription — survive store rebuilds in HMR
      authSubscription?.unsubscribe()
      const { data: subscription } = supabase.auth.onAuthStateChange(
        async (event, newSession) => {
          if (event === 'SIGNED_IN' && newSession) {
            set({
              session: newSession,
              supabaseUser: newSession.user,
              accessToken: newSession.access_token,
              status: 'authenticated',
              isAuthenticated: true,
            })
            await get().refreshProfile()
          } else if (event === 'SIGNED_OUT') {
            set({
              session: null,
              supabaseUser: null,
              accessToken: null,
              user: null,
              status: 'anonymous',
              isAuthenticated: false,
              isLoading: false,
            })
          } else if (event === 'TOKEN_REFRESHED' && newSession) {
            set({
              session: newSession,
              accessToken: newSession.access_token,
            })
          } else if (event === 'USER_UPDATED' && newSession) {
            // Fired after supabase.auth.updateUser succeeds — keep the
            // store's view of user_metadata in sync so reads after
            // saving (e.g. notification prefs) see the new values.
            set({
              session: newSession,
              supabaseUser: newSession.user,
              accessToken: newSession.access_token,
            })
          }
        }
      )
      authSubscription = subscription.subscription
    } catch (error) {
      console.error('Failed to initialize auth session:', describeError(error))
      maybeNotifyNetworkError(error)
      set({
        session: null,
        supabaseUser: null,
        accessToken: null,
        user: null,
        status: 'anonymous',
        isAuthenticated: false,
        isLoading: false,
      })
    }
  },

  signInWithPassword: async (email, password) => {
    set({ isLoading: true })
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      set({ isLoading: false })
      throw error
    }
    // onAuthStateChange will set the session + run refreshProfile
  },

  signInWithMagicLink: async (email) => {
    // Despite the name, this triggers Supabase's signInWithOtp which sends
    // BOTH a 6-digit code and a magic link in the same email. The primary
    // UX is now code entry (see SignupPage); the link is kept as fallback
    // for users who click it from older emails.
    set({ isLoading: true })
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      })
      if (error) throw error
    } finally {
      set({ isLoading: false })
    }
  },

  verifyOtpCode: async (email, token) => {
    set({ isLoading: true })
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      })
      if (error) throw error
      // onAuthStateChange ('SIGNED_IN') will set the session and run
      // refreshProfile. Caller navigates based on the resulting state.
    } finally {
      set({ isLoading: false })
    }
  },

  signOut: async () => {
    try {
      await supabase.auth.signOut()
    } catch (error) {
      console.error('Sign-out error:', describeError(error))
    }
    set({
      user: null,
      session: null,
      supabaseUser: null,
      accessToken: null,
      status: 'anonymous',
      isAuthenticated: false,
      isLoading: false,
    })
  },

  refreshProfile: async () => {
    // Single retry on the cold-start path. The very first /auth/me after the
    // backend has been idle can race the container boot — axios times out,
    // the second attempt usually lands on a warm process. Retrying only on
    // network/timeout errors (not 401/403) so allowlist denials don't loop.
    let lastError: unknown
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const profile = await apiService.getCurrentUser()
        set({ user: profile, isLoading: false })
        return
      } catch (error: any) {
        lastError = error
        const status = error?.response?.status
        if (status === 403) {
          await supabase.auth.signOut()
          set({
            user: null,
            session: null,
            supabaseUser: null,
            accessToken: null,
            status: 'denied',
            isAuthenticated: false,
            isLoading: false,
          })
          toast.error("You're signed in, but your email isn't on the member list yet.")
          return
        }
        if (status === 401) {
          await get().signOut()
          return
        }
        if (!isNetworkOrCorsError(error)) {
          // Some other 4xx/5xx with a response — don't retry blindly.
          break
        }
        if (attempt === 0) {
          // 2s backoff before the second attempt — gives a cold backend a
          // beat to finish booting.
          await new Promise((resolve) => setTimeout(resolve, 2000))
        }
      }
    }
    // Both attempts failed (or we broke out on a non-retryable error).
    console.error('Failed to load profile:', describeError(lastError))
    maybeNotifyNetworkError(lastError)
    // Clear loading so the UI can decide how to recover. AuthCallbackPage
    // watches for status === 'authenticated' && !user and shows a Try-Again
    // panel after a grace period.
    set({ isLoading: false })
  },

  updateUser: (patch) => {
    const { user } = get()
    if (user) set({ user: { ...user, ...patch } })
  },

  setTutorialReplay: (v) => set({ tutorialReplay: v }),
}))
